import { z } from "zod";
/**
 * The persisted secret REFERENCE (decision d15).
 *
 * PlaybookAI stores a pointer to credential material and never the material
 * itself. Before this envelope the pointer was a bare Azure Key Vault URL, which
 * carried the store, the address and the version in one opaque string that only
 * one cloud could produce. The envelope names each of those separately so a
 * second store can be addressed without teaching every reader a new URL shape.
 *
 * The language-neutral definition is
 * `contracts/secret-reference.v1.schema.json`. `services/ais/ais/secret_reference.py`
 * validates against that file directly, and both implementations are driven by
 * the same fixtures (`test/fixtures/secret-references.v1.json`) so they cannot
 * diverge silently.
 *
 * Everything here is fail-closed. A reference that is unrecognised, internally
 * inconsistent, or addressed at a store this deployment does not use is an
 * error, never a best-effort resolution against the wrong secret.
 */
export const SECRET_REFERENCE_SCHEMA_VERSION = 1;
/** The column that persists an encoded reference is `VARCHAR(500)`. */
export const SECRET_REFERENCE_MAX_ENCODED_LENGTH = 500;
export const SECRET_STORE_PROVIDERS = [
    "azure-key-vault",
    "aws-secrets-manager",
];
/**
 * `pinned` resolves one immutable version and is what PlaybookAI writes for
 * credentials it owns, so a rotation is atomic and reversible. `latest` follows
 * operator rotation in the vault without a database write, and is for
 * credentials an operator manages (decision d14).
 */
export const SECRET_REFERENCE_MODES = ["pinned", "latest"];
/**
 * Locator shapes, per store. Deliberately strict: a locator carrying a version
 * would make `mode` a lie, and one addressed at another host would resolve
 * against a store the deployment never configured.
 */
const LOCATOR_PATTERNS = Object.freeze({
    // Versionless Key Vault secret URI. The version lives in `version`, not here.
    "azure-key-vault": /^https:\/\/[a-zA-Z0-9-]+\.vault\.azure\.net\/secrets\/[A-Za-z0-9-]+$/,
    // Full Secrets Manager ARN: it carries the account and region, which is what
    // makes the deployment-boundary check below possible.
    "aws-secrets-manager": /^arn:aws[a-z-]*:secretsmanager:[a-z0-9-]+:[0-9]{12}:secret:[A-Za-z0-9/_+=.@-]+$/,
});
const baseSchema = z
    .object({
    schemaVersion: z.literal(SECRET_REFERENCE_SCHEMA_VERSION),
    provider: z.enum(SECRET_STORE_PROVIDERS),
    mode: z.enum(SECRET_REFERENCE_MODES),
    locator: z.string().min(1).max(320),
    version: z.string().min(1).max(128).optional(),
})
    .strict();
export const secretReferenceSchema = baseSchema.superRefine((value, ctx) => {
    if (value.mode === "pinned" && value.version === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["version"],
            message: "a pinned reference must name the exact provider version",
        });
    }
    if (value.mode === "latest" && value.version !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["version"],
            message: "a latest reference must not name a version; it follows the store's current one",
        });
    }
    if (!LOCATOR_PATTERNS[value.provider].test(value.locator)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["locator"],
            message: `locator is not a valid ${value.provider} address`,
        });
    }
});
export class InvalidSecretReference extends Error {
    constructor(message, options) {
        super(`invalid secret reference: ${message}`, options);
        this.name = "InvalidSecretReference";
    }
}
/** Parses an already-decoded object. Throws rather than returning a partial. */
export function parseSecretReference(value) {
    const parsed = secretReferenceSchema.safeParse(value);
    if (!parsed.success) {
        throw new InvalidSecretReference(parsed.error.issues
            .map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.message}`)
            .join("; "));
    }
    return parsed.data;
}
export function isSecretReference(value) {
    return secretReferenceSchema.safeParse(value).success;
}
/**
 * The persisted form. JSON rather than a compact URI scheme so the two runtimes
 * parse it with a library rather than a regex each maintains separately.
 */
export function encodeSecretReference(reference) {
    const encoded = JSON.stringify(parseSecretReference(reference));
    if (encoded.length > SECRET_REFERENCE_MAX_ENCODED_LENGTH) {
        throw new InvalidSecretReference(`encoded reference is ${encoded.length} characters, over the ${SECRET_REFERENCE_MAX_ENCODED_LENGTH} the column allows`);
    }
    return encoded;
}
export function decodeSecretReference(raw) {
    let value;
    try {
        value = JSON.parse(raw);
    }
    catch (error) {
        // A bare Key Vault URL lands here. After the downtime migration
        // (202609010002) nothing should still hold one, so this is a real fault
        // rather than a legacy shape to fall back on.
        throw new InvalidSecretReference("not a JSON envelope", { cause: error });
    }
    return parseSecretReference(value);
}
export function assertSecretReferenceWithinBoundary(reference, boundary) {
    if (reference.provider !== boundary.provider) {
        throw new InvalidSecretReference(`reference names ${reference.provider} but this deployment resolves ${boundary.provider}`);
    }
    if (reference.provider === "azure-key-vault") {
        const vaultUrl = boundary.vaultUrl;
        if (vaultUrl === undefined) {
            throw new InvalidSecretReference("azure-key-vault boundary requires the deployment vault URL");
        }
        if (originOf(reference.locator) !== originOf(vaultUrl)) {
            throw new InvalidSecretReference("reference does not belong to the configured vault");
        }
        return;
    }
    const { accountId, region } = boundary;
    if (accountId === undefined || region === undefined) {
        throw new InvalidSecretReference("aws-secrets-manager boundary requires the deployment account and region");
    }
    const parts = reference.locator.split(":");
    if (parts[3] !== region || parts[4] !== accountId) {
        throw new InvalidSecretReference("reference does not belong to the configured account and region");
    }
}
function originOf(url) {
    try {
        return new URL(url).origin;
    }
    catch (error) {
        throw new InvalidSecretReference(`not a URL: ${url}`, { cause: error });
    }
}
//# sourceMappingURL=secret-reference.js.map